import type React from 'react';
import { AnyAction } from 'redux';
import { Description, Section } from '../../components/Help/Manual/types';
import { QuestionnaireItem } from '../../components/Cases/CaseInformation';

/**
 * Side-panel width scale.
 *
 * Panels used to carry raw `width: '50%'` / `'70%'` / `'85%'` strings at every
 * dispatch site, which is why the same kind of panel opened at three different
 * widths depending on where it was opened from. Dispatch a `size` instead; the
 * reducer resolves it. `width` still wins when a panel genuinely needs a
 * one-off measurement.
 */
export type SidePanelSize = 'sm' | 'md' | 'lg' | 'xl';

export const SIDE_PANEL_WIDTHS: Record<SidePanelSize, string> = {
  sm: '40%',
  md: '50%',
  lg: '70%',
  xl: '85%',
};

export const DEFAULT_SIDE_PANEL_SIZE: SidePanelSize = 'md';

export interface SidePanelAction {
  label: string;
  variant: "outline" | "success" | "warning" | "neutral" | "primary" | "secondary" | "tertiary" | "danger" | "disabled" | "destructive" | undefined;
  size?: 'small';
  onClick: (instance?: any) => void;
}

interface CommonSidePanelState {
  showSidePanel: boolean;
  /** Preferred: a step on the width scale. */
  size?: SidePanelSize;
  /** Escape hatch for one-off widths (`'32rem'`, `'55%'`…). Wins over `size`. */
  width?: string;
  isExpanded?: boolean;
  onConfirm?: (data?: any) => void;
  onCancel?: () => void;
  loading?: boolean;
  message?: string;
  icon?: any;
  subMsg?: string;
  actions?: SidePanelAction[];
  mb?: boolean;
  hideDefaultHeader?: boolean;
}

interface CollaboratorsPanelState extends CommonSidePanelState {
  type: 'collaboratorsPanel';
  caseId: string;
  summary: any;
  onClose: () => void;
  onUpdate: () => void;
}

interface QuestionnairePanelState extends CommonSidePanelState {
  type: 'questionnairePanel';
  matterId: string;
  questionIndex: number;
  item?: QuestionnaireItem;
  onClose: () => void;
  onUpdate: () => void;
}


/**
 * A task attachment, read in the global drawer.
 *
 * Separate from `reader` because this one carries the attachment's own facts
 * - size, type, uploader, date - which the pane shows above the document. The
 * generic reader has a citation and nothing else to say about the file.
 */
/**
 * What a table's bulk upload actually did.
 *
 * A run of 180 documents can lose some of them for three different reasons —
 * refused on content, failed in transport, uploaded but not accepted for
 * processing - and a toast has room for none of that. The panel lists all
 * three and can retry just the transport failures.
 */
interface TableUploadReportState extends CommonSidePanelState {
  type: 'tableUploadReport';
  /** See `TableUploadReport` for the shape; kept loose to keep the store free
      of component imports. */
  data: any;
}

interface TaskAttachmentReaderState extends CommonSidePanelState {
  type: 'taskAttachmentReader';
  /** `TaskReaderContent`, typed loosely here to keep the store free of
      component imports - the panel narrows it on the way in. */
  data: any;
}

interface ReaderTypeState extends CommonSidePanelState {
  type: 'reader';
  citation: any;
  matterId: string;
  sourceType: string;
  userAccessLevel?: 'edit' | 'admin' | 'view';
  onDelete?: () => void;
}

interface DraftTypeState extends CommonSidePanelState {
  type: 'draftTypes';
  caseId: string;
  onSave?: () => void;
}

interface DraftTypeFilesState extends CommonSidePanelState {
  type: 'draftTypeFiles';
  caseId: string;
  draftTypeName: string;
}

interface DraftSettingsState extends CommonSidePanelState {
  type: 'draftSettings';
  matterId: string;
  draftId: number;
  onSave?: () => void;
}

interface WorkflowState extends CommonSidePanelState {
  type: 'workflows';
  caseId: string;
  onSave?: () => void;
}


interface ExtractedContentSidePanelState extends CommonSidePanelState {
  type: 'extractedContent';
  content: string;
  filename: string;
}

interface EmployeeTaskDetailSidePanelState extends CommonSidePanelState {
  type: 'employeeTaskDetail';
  taskData: any;
}

interface EmployeeCaseDetailSidePanelState extends CommonSidePanelState {
  type: 'employeeCaseDetail';
  caseData: any;
}

interface ProposeCaseSidePanelState extends CommonSidePanelState {
  type: 'proposeCase';
  lawyerId: string;
}

interface AddChronologyEventSidePanelState extends CommonSidePanelState {
  type: 'addChronologyEvent';
  caseId: string;
  onSuccess?: () => void;
}

interface AddLegalOutlineTemplateState extends CommonSidePanelState {
  type: 'addLegalOutlineTemplate';
  caseId: string;
  onFinish: () => void;
}

interface CreateIndexState extends CommonSidePanelState {
  type: 'createIndex';
}

interface LegalOutlineContentSidePanelState extends CommonSidePanelState {
  type: 'legalOutlineContent';
  sectionName: string;
  content: string;
  fieldData: { text: string; files: File[] };
  onSave: (newContent: string | null, newFiles: File[]) => void;
}

interface LegalOutlineGeneratedContentSidePanelState extends CommonSidePanelState {
  type: 'legalOutlineGeneratedContent';
  sectionName: string;
  content: string;
}

interface ChronologyFileContentSidePanelState extends CommonSidePanelState {
  type: 'chronologyFileContent';
  content: string;
  label: string;
}

interface FileReaderSidePanelState extends CommonSidePanelState {
  type: 'fileReader';
  fileUrl: string;
  fileType: string;
  loading: boolean;
}

interface FileReaderTstSidePanelState extends CommonSidePanelState {
  type: 'fileReaderTst';
  fileUrl?: string;
  content?: string;
  isPdf?: boolean;
  filename: string;
  loading: boolean;
}

interface SupportTicketFormSidePanelState extends CommonSidePanelState {
  type: 'supportTicketForm';
}

interface PlaceBidSidePanelState extends CommonSidePanelState {
  type: 'placeBid';
  engagement: any;
}

interface AddTaskSidePanelState extends CommonSidePanelState {
  type: 'addTask';
}


interface SupportTicketSidePanelState extends CommonSidePanelState {
  type: 'supportTicket';
}

interface HelpContentSidePanelState extends CommonSidePanelState {
  type: 'helpContent';
  content: {
    section: Section;
    description: Description;
  };
}

interface OnboardingSidePanelState extends CommonSidePanelState {
  type: 'onboarding';
}

interface CaseViewSidePanelState extends CommonSidePanelState {
  type: 'caseView';
  caseItem: any;
}

interface CustomSidePanelState extends CommonSidePanelState {
  type: 'custom';
}

interface AddClientSidePanelState extends CommonSidePanelState {
  type: 'addClient';
  onClientCreated?: (client: any) => void;
}

interface TimekeepingSidePanelState extends CommonSidePanelState {
  type: 'timekeepingView';
  day: string | null;
  data: any;
  isLoading: boolean;
  stats: {
    clocked_hours: number;
    negative_hours: number;
    overtime: number;
    total_hours_worked: number;
    tasks: string[];
    casesWorkedOn: number;
  } | null;
}

interface CreateProjectSidePanelState extends CommonSidePanelState {
  type: 'createProject';
}

interface AddCaseSidePanelState extends CommonSidePanelState {
  type: 'addCase';
  clientId: string | undefined;
  clientUserType: string | undefined;
}

interface PublishCasePanelState extends CommonSidePanelState {
  type: 'publishCase';
  caseItem: any;
}

interface AddTeamPanelState extends CommonSidePanelState {
  type: 'addTeam';
}

interface TimeEntryDetailsSidePanelState extends CommonSidePanelState {
  type: 'timeEntryDetails';
  data: any;
}

interface TransationDetails extends CommonSidePanelState {
  type: 'transactionDetails';
  transaction_id: string;
}

interface DocumentCustomizationSidePanelState extends CommonSidePanelState {
  type: 'documentCustomization';
  matterUuid: string;
  documentTypeId: string;
  documentType: 'draft' | 'insight';
  documentName: string;
}

interface LicenseDetailSidePanelState extends CommonSidePanelState {
  type: 'licenseDetail';
  plan: any;
  billingPeriod: 'monthly' | 'annual';
  isCurrentPlan: boolean;
  isActive: boolean;
  onAction?: (plan: any) => void;
  currentPlanSortOrder?: number;
  seatCount?: number;
  seatsUsed?: number;
  onUpdateSeats?: (newQuantity: number) => Promise<void>;
}

interface AssignLicenseSidePanelState extends CommonSidePanelState {
  type: 'assignLicense';
  userIds: number[];
  userNames: string[];
  onComplete?: () => void;
}

interface ProjectResourcesSidePanelState extends CommonSidePanelState {
  type: 'projectResources';
  projectUuid: string;
  onResourceLinked?: () => void;
}

/**
 * The other direction from `projectResources`: that panel picks resources for
 * a project, this one picks projects for a resource. Used by `ProjectBadges`,
 * so every entity that renders those badges (matter, task, table, review,
 * template, data source) gets the same panel.
 */
interface ConnectToProjectSidePanelState extends CommonSidePanelState {
  type: 'connectToProject';
  resourceType: string;
  resourceUuid: string;
  onChanged?: () => void;
}

/**
 * The matter's own picker: which research / drafts / insights / reviews /
 * tables are attached to THIS matter. Distinct from `connectToProject` because
 * the relation, the endpoint and the vocabulary are the matter's, not the
 * project resource-link table's.
 */
interface ConnectToMatterSidePanelState extends CommonSidePanelState {
  type: 'connectToMatter';
  matterUuid: string;
  initialType?: string;
  onChanged?: () => void;
}

/**
 * A task's own matter link. Distinct from `connectToMatter`, which attaches
 * research/drafts/reviews to a matter through the matter's connected-work
 * table: a task carries a single `matter_uuid` on the record itself.
 */
interface LinkTaskToMatterSidePanelState extends CommonSidePanelState {
  type: 'linkTaskToMatter';
  taskUuid: string;
  currentMatter?: { uuid?: string; title?: string } | null;
  onChanged?: () => void;
}

interface UploadAttachmentSidePanelState extends CommonSidePanelState {
  type: 'uploadAttachment';
  files?: File[];
}


interface AddSourceSidePanelState extends CommonSidePanelState {
  type: 'addSource';
  sourceType: 'law' | 'case';
}

interface AddClientToMatterSidePanelState extends CommonSidePanelState {
  type: 'addClientToMatter';
  matterUuid: string;
  allClients: any[];
  clientRoleOptions: any[];
  selectedClients: string[];
  onRefresh?: () => void | Promise<void>;
  onAfterSave?: () => void | Promise<void>;
}

interface AddTableDocumentSidePanelState extends CommonSidePanelState {
  type: 'addTableDocument';
  tableUuid: string;
}

interface SaveTableTemplateSidePanelState extends CommonSidePanelState {
  type: 'saveTableTemplate';
  tableUuid: string;
  defaultName: string;
}

interface ResourceCollaboratorsSidePanelState extends CommonSidePanelState {
  type: 'manageResourceCollaborators';
  resourceUuid: string;
  resourceType: string;
  collaborators: any[];
  currentUserUuid?: string;
  onUpdate?: () => void;
  canAdmin?: boolean;
  addCollaborator: (resourceUuid: string, userUuid: string, access: string, collab?: any) => Promise<any>;
  removeCollaborator: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  updateAccess: (resourceUuid: string, collaboratorUuid: string, access: string) => Promise<any>;
  // Header customization
  title?: string;
  subMessage?: string;
  icon?: React.ReactNode;
  // Lead extension (clients use this)
  supportsLead?: boolean;
  isLead?: (collab: any) => boolean;
  setLead?: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  ownerLabel?: string;
}

interface ClauseRuleFormPanelState extends CommonSidePanelState {
  type: 'editClauseRule';
  reviewUuid: string;
  rule?: any;
}

interface GlobalRuleFormPanelState extends CommonSidePanelState {
  type: 'editGlobalRule';
  reviewUuid: string;
  rule?: any;
}

/**
 * One matter time entry, opened by clicking its row in the Time keeping list
 * (mat-16). Carries the row plus what the panel needs to label and write it.
 */
interface MatterTimeEntryPanelState extends CommonSidePanelState {
  type: 'matterTimeEntry';
  entry: any;
  matterUuid: string;
  activityTypes: any[];
  fallbackLabel?: string;
  canManage: boolean;
  /** False on the personal timesheet, which owns no delete confirmation. */
  allowDelete?: boolean;
  onSaved: () => void | Promise<void>;
  onDeleteEntry: (entry: any) => void;
}

/**
 * Matter pricing, edited inside the Economics tab (mat-19). Details renders
 * the same reading view-only and links here.
 */
interface MatterPricingPanelState extends CommonSidePanelState {
  type: 'matterPricing';
  matterData: any;
  currency: string;
  budgetAmount: number | null;
  onSaved?: (patch: Record<string, any>) => void;
}

/**
 * One item's AI usage, opened from an org-level usage breakdown.
 *
 * The drill-down behind "By resource" on the Settings AI-usage surfaces —
 * the same pane the matter and draft workspaces dock. There is no cost flag
 * to carry: every surface this pane appears on is customer-facing, so it
 * shows tokens and operations and no money, always (see `AIUsagePanel`).
 */
interface AIUsageDetailPanelState extends CommonSidePanelState {
  type: 'aiUsageDetail';
  entityType: string;
  entityId: string;
  entityName?: string;
}

/**
 * Project pricing (proj-7). The same five fields a matter carries, at the
 * basic level: no time entries behind them, so no revenue confirmation.
 */
interface ProjectPricingPanelState extends CommonSidePanelState {
  type: 'projectPricing';
  projectUuid: string;
  project: any;
  onSaved?: (patch: Record<string, any>) => void;
}

/**
 * Editing a contact's details (con-3). The Details tab used to flip its one
 * card into a form in place; every other record edits in a side panel, so a
 * contact does too.
 */
interface EditContactSidePanelState extends CommonSidePanelState {
  type: 'editContact';
  client: any;
  /** The `/dropdown/client-types` list, already loaded by the page. */
  contactTypes: any[];
  onSaved?: (client: any) => void;
}

interface BulkReviewRepaperPanelState extends CommonSidePanelState {
  type: 'bulkReviewRepaper';
  table: any;
  preselectedRowIds?: number[];
}

/**
 * DraftingCustomize - regen/customise side panel for an existing drafted
 * document on the Drafting detail page (AB#1573).
 * Opened from DraftingDetail via `dispatch(showSidePanel({ type: 'draftingCustomize', … }))`
 * at `size: 'lg'` - it used to dispatch a one-off `width: '68%'`, which is the
 * kind of hand-measured width the scale above exists to end.
 */
interface DraftingCustomizeSidePanelState extends CommonSidePanelState {
  type: 'draftingCustomize';
  /** draft | strategy */
  kind: 'draft' | 'strategy';
  /** UUID of the existing drafting item to regenerate. */
  draftingUuid?: string;
  /** Full item data for pre-populating fields. */
  existingItem?: Record<string, any>;
  /** Legacy: callback after creation (creation now uses DraftingWizard page). */
  onCreated?: (uuid: string) => void;
}

/**
 * The dashboard's "add widget" gallery.
 *
 * Carries ids rather than definitions: the panel looks the label, description
 * and icon up in the widget registry, so a payload stays a plain value.
 */
interface DashboardWidgetsSidePanelState extends CommonSidePanelState {
  type: 'dashboardWidgets';
  /** Registered, currently hidden and licensed - in registration order. */
  available: string[];
  /**
   * Registered, licensed and ALREADY on the dashboard.
   *
   * The panel lists the whole catalogue, not just the leftovers: one that
   * offered two cards read as "this is all a dashboard can be" when it meant
   * "you already have the other nine".
   */
  placed?: string[];
  onAdd: (id: any) => void;
  /** Take one off again, without leaving the panel. */
  onRemove?: (id: any) => void;
}

export type SidePanelState =
  | ConnectToProjectSidePanelState
  | ConnectToMatterSidePanelState
  | LinkTaskToMatterSidePanelState
  | DashboardWidgetsSidePanelState
  | BulkReviewRepaperPanelState
  | AddTaskSidePanelState
  | SupportTicketSidePanelState
  | HelpContentSidePanelState
  | OnboardingSidePanelState
  | CaseViewSidePanelState
  | AddClientSidePanelState
  | EditContactSidePanelState
  | CustomSidePanelState
  | TimekeepingSidePanelState
  | CreateProjectSidePanelState
  | AddCaseSidePanelState
  | PublishCasePanelState
  | AddTeamPanelState
  | PlaceBidSidePanelState
  | ProposeCaseSidePanelState
  | SupportTicketFormSidePanelState
  | FileReaderSidePanelState
  | LegalOutlineContentSidePanelState
  | LegalOutlineGeneratedContentSidePanelState
  | ChronologyFileContentSidePanelState
  | AddChronologyEventSidePanelState
  | AddLegalOutlineTemplateState
  | CreateIndexState
  | TimeEntryDetailsSidePanelState
  | EmployeeTaskDetailSidePanelState
  | EmployeeCaseDetailSidePanelState
  | ExtractedContentSidePanelState
  | TransationDetails
  | WorkflowState
  | FileReaderTstSidePanelState
  | DraftTypeState
  | DraftTypeFilesState
  | DraftSettingsState
  | ReaderTypeState
  | TaskAttachmentReaderState
  | TableUploadReportState
  | QuestionnairePanelState
  | CollaboratorsPanelState
  | DocumentCustomizationSidePanelState
  | LicenseDetailSidePanelState
  | AssignLicenseSidePanelState
  | ProjectResourcesSidePanelState
  | UploadAttachmentSidePanelState
  | AddSourceSidePanelState
  | AddClientToMatterSidePanelState
  | AddTableDocumentSidePanelState
  | SaveTableTemplateSidePanelState
  | ResourceCollaboratorsSidePanelState
  | ClauseRuleFormPanelState
  | GlobalRuleFormPanelState
  | DraftingCustomizeSidePanelState
  | TemplatePreviewSidePanelState
  | TemplateMarkdownEditSidePanelState
  | MatterTimeEntryPanelState
  | MatterPricingPanelState
  | ProjectPricingPanelState
  | ScanSidePanelState
  | AIUsageDetailPanelState;

/** Legislation Scan - runs a document against legislation / data sources. */
export interface ScanSidePanelState extends CommonSidePanelState {
  type: 'scan';
  /** Discriminated target: a draft, an uploaded file, or a review document. */
  target:
    | { kind: 'draft'; draftUuid: string }
    | { kind: 'file'; fileUuid: string }
    | { kind: 'reviewDocument'; sessionUuid: string; documentId: number };
  language?: string | null;
}

interface TemplatePreviewSidePanelState extends CommonSidePanelState {
  type: 'templatePreview';
  templateId: string;
  templateName: string;
  isGlobal?: boolean;
  outline?: Array<{ title?: string; section?: string; prompt?: string; description?: string }>;
  description?: string;
}

interface TemplateMarkdownEditSidePanelState extends CommonSidePanelState {
  type: 'templateMarkdownEdit';
  templateId: string;
  templateName?: string;
}

const initialState: SidePanelState = {
  showSidePanel: false,
  type: 'custom',
  size: DEFAULT_SIDE_PANEL_SIZE,
  width: SIDE_PANEL_WIDTHS[DEFAULT_SIDE_PANEL_SIZE],
  isExpanded: false,
  loading: false,
  message: '',
  mb: true
};

export const sidePanelReducer = (
  state = initialState,
  action: AnyAction
): SidePanelState => {
  switch (action.type) {
    case 'SHOW_SIDE_PANEL': {
      const payload = action.payload as Partial<SidePanelState>;
      const size = payload.size ?? DEFAULT_SIDE_PANEL_SIZE;
      return {
        ...state,
        ...action.payload,
        size,
        // Resolved here rather than spread through, so a panel opened without a
        // width never inherits the previous panel's.
        width: payload.width ?? SIDE_PANEL_WIDTHS[size],
        showSidePanel: true,
        loading: action.payload.type === 'fileReader' || action.payload.type === 'fileReaderTst' ? true : state.loading,
      };
    }
    case 'HIDE_SIDE_PANEL':
      return {
        ...initialState,
        showSidePanel: false,
        isExpanded: false,
        loading: false,
      };
    case 'EXPAND_SIDE_PANEL':
      return {
        ...state,
        isExpanded: true,
        width: SIDE_PANEL_WIDTHS.xl,
      };
    case 'COLLAPSE_SIDE_PANEL':
      return {
        ...state,
        isExpanded: false,
        // Back to the panel's own declared width, not a blanket 50%.
        width: SIDE_PANEL_WIDTHS[state.size ?? DEFAULT_SIDE_PANEL_SIZE],
      };
    case 'UPDATE_SIDE_PANEL':
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

export default sidePanelReducer;
